/**
 * DomainTlsTab — 收件域右侧"TLS 安全"Tab
 * 该域名的证书 / ACME 自动签发 / 最小 TLS 版本（每地址按域名走，SNI 选择证书）。
 * 协议级开关（STARTTLS/IMAPS/POP3S）为服务器全局，以"全局"区段展示。
 */
import { useState, useRef, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import {
  makeStyles,
  tokens,
  Text,
  Input,
  Button,
  Spinner,
  Switch,
  Field,
  InfoLabel,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Card,
  Combobox,
  Option,
  Divider,
} from '@fluentui/react-components'
import {
  ShieldRegular,
  ArrowUploadRegular,
  FlashRegular,
  CheckRegular,
  ArrowSyncRegular,
} from '@fluentui/react-icons'
import { domainTlsApi } from '@/api/domainTls'
import { tlsApi } from '@/api/tls'
import type { Domain, DomainAcmeStatus } from '@/api/types'
import { toast } from '@/lib/toast'
import { ApiError } from '@/api/client'

const useStyles = makeStyles({
  root: { display: 'flex', flexDirection: 'column', gap: '16px', minWidth: 0 },
  form: { display: 'flex', flexDirection: 'column', gap: '14px' },
  readOnly: { fontFamily: 'monospace', fontSize: '13px' },
  descRow: { fontSize: '13px', color: tokens.colorNeutralForeground3, marginTop: '4px' },
  statusTable: { display: 'flex', flexDirection: 'column', gap: '8px' },
  statusRow: { display: 'flex', justifyContent: 'space-between', fontSize: '14px' },
  uploadArea: { display: 'flex', flexDirection: 'column', gap: '8px' },
  certFileLabel: { fontSize: '13px' },
})

export default function DomainTlsTab({ domain }: { domain: Domain }) {
  const styles = useStyles()
  const queryClient = useQueryClient()
  const certRef = useRef<HTMLInputElement>(null)
  const keyRef = useRef<HTMLInputElement>(null)

  const [certFile, setCertFile] = useState<File | null>(null)
  const [keyFile, setKeyFile] = useState<File | null>(null)
  const [minVersion, setMinVersion] = useState('1.2')
  const [acme, setAcme] = useState({
    acme_enabled: false,
    acme_email: '',
    acme_domains: '',
    acme_auto_renew: true,
  })
  const syncedRef = useRef(false)

  // 每域名 TLS 配置
  const { data: config, isLoading } = useQuery({
    queryKey: ['domain-tls', domain.id],
    queryFn: () => domainTlsApi.get(domain.id),
  })

  useEffect(() => {
    syncedRef.current = false
    setCertFile(null)
    setKeyFile(null)
  }, [domain.id])

  useEffect(() => {
    if (config && !syncedRef.current) {
      syncedRef.current = true
      setMinVersion(config.min_version || '1.2')
      setAcme({
        acme_enabled: config.acme_enabled,
        acme_email: config.acme_email,
        acme_domains: (config.acme_domains || []).join(', '),
        acme_auto_renew: config.acme_auto_renew,
      })
    }
  }, [config])

  // 全局协议开关（服务器级，仅展示/保存全局）
  const { data: globalConfig } = useQuery({
    queryKey: ['tls-config'],
    queryFn: () => tlsApi.getConfig(),
  })
  const [proto, setProto] = useState({ starttls_enabled: true, imaps_enabled: true, pop3s_enabled: true })
  const protoSynced = useRef(false)
  useEffect(() => {
    if (globalConfig && !protoSynced.current) {
      protoSynced.current = true
      setProto({
        starttls_enabled: globalConfig.starttls_enabled,
        imaps_enabled: globalConfig.imaps_enabled,
        pop3s_enabled: globalConfig.pop3s_enabled,
      })
    }
  }, [globalConfig])

  const uploadMutation = useMutation({
    mutationFn: () => {
      if (!certFile || !keyFile) throw new ApiError(400, '请同时选择证书文件和私钥文件')
      return domainTlsApi.uploadCert(domain.id, certFile, keyFile)
    },
    onSuccess: async () => {
      toast.success('上传成功', '证书已上传并热重载')
      await queryClient.invalidateQueries({ queryKey: ['domain-tls', domain.id] })
      setCertFile(null)
      setKeyFile(null)
    },
    onError: (err) => toast.error('上传失败', err instanceof ApiError ? err.message : '上传失败'),
  })

  const saveMinVersionMutation = useMutation({
    mutationFn: () => domainTlsApi.put(domain.id, { min_version: minVersion }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['domain-tls', domain.id] })
      toast.success('已保存', '最小 TLS 版本已保存并生效')
    },
    onError: (err) => toast.error('保存失败', err instanceof ApiError ? err.message : '保存失败'),
  })

  // 全局协议开关保存
  const saveProtoMutation = useMutation({
    mutationFn: () =>
      tlsApi.putConfig({
        starttls_enabled: proto.starttls_enabled,
        imaps_enabled: proto.imaps_enabled,
        pop3s_enabled: proto.pop3s_enabled,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['tls-config'] })
      toast.success('已保存', '协议开关已保存并热重载生效')
    },
    onError: (err) => toast.error('保存失败', err instanceof ApiError ? err.message : '保存失败'),
  })

  const testMutation = useMutation({
    mutationFn: () => domainTlsApi.test(domain.id),
    onSuccess: (res) => {
      if (res.ok) toast.success('证书有效', `剩余 ${res.cert?.days_remaining ?? '?'} 天`)
      else toast.error('证书无效', res.error || '证书校验未通过')
    },
    onError: (err) => toast.error('测试失败', err instanceof ApiError ? err.message : '测试失败'),
  })

  const saveAcmeMutation = useMutation({
    mutationFn: () =>
      domainTlsApi.put(domain.id, {
        acme_enabled: acme.acme_enabled,
        acme_email: acme.acme_email,
        acme_domains: acme.acme_domains.split(',').map((s) => s.trim()).filter(Boolean),
        acme_auto_renew: acme.acme_auto_renew,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['domain-tls', domain.id] })
      toast.success('ACME 设置已保存')
    },
    onError: (err) => toast.error('保存失败', err instanceof ApiError ? err.message : '保存失败'),
  })

  // ACME 状态
  const [acmeStatus, setAcmeStatus] = useState<DomainAcmeStatus | null>(null)
  const acmeStatusMutation = useMutation({
    mutationFn: () => domainTlsApi.acmeStatus(domain.id),
    onSuccess: (data) => {
      setAcmeStatus(data)
      toast.success('ACME 状态已获取', data.message || '')
    },
    onError: (err) => toast.error('获取失败', err instanceof ApiError ? err.message : '获取 ACME 状态失败'),
  })

  const issueAcmeMutation = useMutation({
    mutationFn: () => domainTlsApi.issueAcme(domain.id),
    onSuccess: async (res) => {
      toast.success('签发成功', res.message || '证书已签发')
      await queryClient.invalidateQueries({ queryKey: ['domain-tls', domain.id] })
    },
    onError: (err) => toast.error('签发失败', err instanceof ApiError ? err.message : '证书签发失败'),
  })

  const cert = config?.cert
  let certBanner: React.ReactNode = null
  if (config) {
    if (!cert || !cert.present) {
      certBanner = (
        <MessageBar intent="warning">
          <MessageBarBody>该域名尚未配置证书：请上传 TLS 证书或启用 ACME 自动签发，否则此域名的 SMTP/IMAP/POP3 加密传输不可用。</MessageBarBody>
        </MessageBar>
      )
    } else if (cert.valid) {
      certBanner = (
        <MessageBar intent="success">
          <MessageBarBody>
            证书有效 ｜ 颁发者：{cert.issuer} ｜ 到期：{dayjs.unix(cert.not_after).format('YYYY-MM-DD')} ｜ 剩余 {cert.days_remaining} 天 ｜ 域名：{(cert.domains || []).join(', ')}
          </MessageBarBody>
        </MessageBar>
      )
    } else {
      certBanner = (
        <MessageBar intent="error">
          <MessageBarBody>证书已过期或无效：{cert.error || `到期：${dayjs.unix(cert.not_after).format('YYYY-MM-DD')}`}</MessageBarBody>
        </MessageBar>
      )
    }
  }

  return (
    <div className={styles.root}>
      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}><Spinner size="large" /></div>
      ) : (
        <>
          <MessageBar intent="info">
            <MessageBarBody>
              <MessageBarTitle>按域名配置</MessageBarTitle>
              以下证书与 ACME 仅作用于 <b>{domain.name}</b>，通过 SNI 为不同收件域选择对应证书。
            </MessageBarBody>
          </MessageBar>

          {certBanner}

          <Card size="small">
            <Text style={{ fontWeight: 600 }}>证书文件</Text>
            <div className={styles.uploadArea} style={{ marginTop: 12 }}>
              <div>
                <div className={styles.certFileLabel}>证书文件路径 (fullchain.pem)</div>
                <Input className={styles.readOnly} value={config?.cert_path || '（未配置）'} readOnly />
              </div>
              <div>
                <div className={styles.certFileLabel}>私钥文件路径 (privkey.pem)</div>
                <Input className={styles.readOnly} value={config?.key_path || '（未配置）'} readOnly />
              </div>
              <input ref={certRef} type="file" accept=".pem,.crt" hidden onChange={(e) => { setCertFile(e.target.files?.[0] ?? null); e.target.value = '' }} />
              <input ref={keyRef} type="file" accept=".pem,.key" hidden onChange={(e) => { setKeyFile(e.target.files?.[0] ?? null); e.target.value = '' }} />
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <Button icon={<ArrowUploadRegular />} onClick={() => certRef.current?.click()}>选择证书文件 {certFile ? `：${certFile.name}` : ''}</Button>
                <Button icon={<ArrowUploadRegular />} onClick={() => keyRef.current?.click()}>选择私钥文件 {keyFile ? `：${keyFile.name}` : ''}</Button>
                <Button appearance="primary" icon={uploadMutation.isPending ? <Spinner size="tiny" /> : <ShieldRegular />} disabled={!certFile || !keyFile} onClick={() => uploadMutation.mutate()}>上传并应用</Button>
              </div>
              <Text className={styles.descRow}>支持 .pem / .crt / .key，上传后自动验证并热重载</Text>
            </div>
          </Card>

          <Divider />

          <Card size="small">
            <Text style={{ fontWeight: 600 }}>协议设置</Text>
            <div className={styles.form} style={{ marginTop: 12 }}>
              <Field label={<InfoLabel info="该域名协商 TLS 时接受的最低版本。">最小 TLS 版本</InfoLabel>}>
                <Combobox
                  value={minVersion}
                  selectedOptions={[minVersion]}
                  onOptionSelect={(_, d) => setMinVersion(d.selectedOptions[0])}
                >
                  <Option value="1.2">TLS 1.2（推荐）</Option>
                  <Option value="1.3">TLS 1.3</Option>
                </Combobox>
              </Field>
              <div style={{ display: 'flex', gap: '8px' }}>
                <Button appearance="primary" icon={<FlashRegular />} disabled={saveMinVersionMutation.isPending} onClick={() => saveMinVersionMutation.mutate()}>保存最小版本</Button>
                <Button icon={testMutation.isPending ? <Spinner size="tiny" /> : <CheckRegular />} onClick={() => testMutation.mutate()}>测试证书</Button>
              </div>
            </div>
          </Card>

          <Divider />

          <MessageBar intent="info">
            <MessageBarBody>
              <MessageBarTitle>服务器全局</MessageBarTitle>
              以下协议开关作用于整个服务器（所有收件域），与上方"按域名"的证书配置维度不同。
            </MessageBarBody>
          </MessageBar>

          <Card size="small">
            <Text style={{ fontWeight: 600 }}>协议开关（全局）</Text>
            <div className={styles.form} style={{ marginTop: 12 }}>
              <Switch label="启用 STARTTLS（SMTP 端口 25/587）" checked={proto.starttls_enabled} onChange={(_, d) => setProto((p) => ({ ...p, starttls_enabled: d.checked }))} />
              <Switch label="启用 IMAPS（隐式 TLS 端口 993）" checked={proto.imaps_enabled} onChange={(_, d) => setProto((p) => ({ ...p, imaps_enabled: d.checked }))} />
              <Switch label="启用 POP3S（隐式 TLS 端口 995）" checked={proto.pop3s_enabled} onChange={(_, d) => setProto((p) => ({ ...p, pop3s_enabled: d.checked }))} />
              <div>
                <Button appearance="primary" icon={<FlashRegular />} disabled={saveProtoMutation.isPending} onClick={() => saveProtoMutation.mutate()}>保存并应用</Button>
              </div>
            </div>
          </Card>

          <Divider />

          <Card size="small">
            <MessageBar intent="info" style={{ marginBottom: 12 }}>
              <MessageBarBody>
                通过 Let's Encrypt 自动签发证书并在到期前自动续期。前提：签发域名必须已解析（A 记录指向本服务器）且 80 端口可被公网访问。
              </MessageBarBody>
            </MessageBar>
            <Text style={{ fontWeight: 600 }}>ACME 自动签发（{domain.name}）</Text>
            <div className={styles.form} style={{ marginTop: 12 }}>
              <Switch label="启用 ACME 自动签发" checked={acme.acme_enabled} onChange={(_, d) => setAcme((a) => ({ ...a, acme_enabled: d.checked }))} />
              <Field label={<InfoLabel info="作为证书注册联系人，用于证书到期 / 续期失败等通知。建议使用 postmaster@你的域名。">联系邮箱</InfoLabel>}>
                <Input value={acme.acme_email} onChange={(_, d) => setAcme((a) => ({ ...a, acme_email: d.value }))} placeholder="postmaster@example.com（用于证书过期通知）" />
              </Field>
              <Field label={<InfoLabel info="要签发证书的域名，多个用逗号分隔（将作为 SAN 一并签发）。必须已解析指向本服务器（A 记录），否则 ACME 域验证会失败。">签发域名</InfoLabel>}>
                <Input value={acme.acme_domains} onChange={(_, d) => setAcme((a) => ({ ...a, acme_domains: d.value }))} placeholder="mail.example.com（多个域名用逗号分隔）" />
              </Field>
              <Switch label="到期前 30 天自动续期" checked={acme.acme_auto_renew} onChange={(_, d) => setAcme((a) => ({ ...a, acme_auto_renew: d.checked }))} />
              <div style={{ display: 'flex', gap: '8px' }}>
                <Button appearance="primary" disabled={saveAcmeMutation.isPending} onClick={() => saveAcmeMutation.mutate()}>保存设置</Button>
                <Button icon={acmeStatusMutation.isPending ? <Spinner size="tiny" /> : <ArrowSyncRegular />} onClick={() => acmeStatusMutation.mutate()}>检查状态</Button>
                <Button appearance="subtle" icon={issueAcmeMutation.isPending ? <Spinner size="tiny" /> : <ShieldRegular />} disabled={issueAcmeMutation.isPending} onClick={() => issueAcmeMutation.mutate()}>手动申请证书</Button>
              </div>
            </div>

            {acmeStatus && (
              <Card size="small" style={{ marginTop: 16 }}>
                <Text style={{ fontWeight: 600 }}>ACME 状态</Text>
                <div className={styles.statusTable} style={{ marginTop: 12 }}>
                  <div className={styles.statusRow}><span>启用</span><span>{acmeStatus.enabled ? '已启用' : '未启用'}</span></div>
                  <div className={styles.statusRow}><span>签发域名</span><span>{(acmeStatus.domains || []).join(', ') || '-'}</span></div>
                  <div className={styles.statusRow}><span>上次签发</span><span>{acmeStatus.last_issued ?? '-'}</span></div>
                  <div className={styles.statusRow}><span>下次续期</span><span>{acmeStatus.next_renewal ?? '-'}</span></div>
                </div>
              </Card>
            )}
          </Card>
        </>
      )}
    </div>
  )
}